import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
} from "reactflow";

import "reactflow/dist/style.css";
import StartEndNode from "./nodes/StartEndNode";
import ActionNode from "./nodes/ActionNode";
import UtilityNode from "./nodes/UtilityNode";
import CodeNode from "./nodes/CodeNode";
import DecisionNode from "./nodes/DecisionNode";
import FilterNode from "./nodes/FilterNode";
import FormatNode from "./nodes/FormatNode";
import MissionControlNode from "./nodes/MissionControlNode";
import RunPlaybookNode from "./nodes/PlaybookNode";
import PromptNode from "./nodes/PromptNode";
import PlaybookEdge from "./edges/PlaybookEdge";
import InfoCard from "./cards/InfoCard";
import { ResetLayoutControl, LicenseControl, ImageDownloadControl, DocumentDownloadControl, VectorGraphicDownloadControl } from "./controls/Controls";
import { LabelColors } from "./constants";
import PlaybookInfoCard from "./cards/PlaybookInfoCard";

const initialNodes = [];
const initialEdges = [];

function generateNodes(playbook) {
  let playbookNodes = Object.values(playbook.coa.data.nodes).map((node) => {
    let type = "default";
    let label = node.data.type;

    switch (node.data.type) {
      case "filter":
        type = "filterNode";
        break;
      case "action":
        type = "actionNode";
        label = node.data.action;
        break;
      case "utility":
        type = "utilityNode";
        label = node.data.functionName;
        break;
      case "start":
        type = "startEndNode";
        node.data["isStart"] = true;
        node.data["inputSpec"] = playbook["coa"]["input_spec"]
        break;
      case "end":
        type = "startEndNode";
        node.data["isStart"] = false;
        node.data["inputSpec"] = playbook["coa"]["output_spec"]
        break;
      case "decision":
        type = "decisionNode";
        break;
      case "code":
        type = "codeNode";
        label = node.data.functionName;
        break;
      case "format":
        type = "formatNode";
        label = node.data["functionName"];
        break;
      case "playbook":
        type = "runPlaybookNode";
        label = node.data["functionName"];
        break;
      case "prompt":
        type = "promptNode";
        label = node.data.type;
        break;
      case "missionControl":
        type = "missionControlNode";
        label = node.data["action"];
        break;
      default:
        type = "default";
        label = node.data.type;
        break;
    }

    return {
      id: node.id,
      type: type,
      data: { label: label, ...node.data },
      position: { x: node.x, y: node.y },
    };
  });

  return playbookNodes;
}

function generateEdges(playbook) {
  let playbookEdges = playbook.coa.data.edges.map((edge) => {
    let data = {};

    let colors = LabelColors;

    if ("conditions" in edge) {
      let index = edge["conditions"][0]["index"];

      let conditions =
        playbook.coa.data.nodes[edge.sourceNode]["data"]["conditions"];
      const cond = conditions.find(
        (condition) => condition.conditionIndex === index
      );

      data["label"] = `${index + 1} ${cond.customName ? ": " + cond.customName : ''}`;
      data["bgFill"] = colors[index];
    }
    return {
      id: edge.id,
      source: edge.sourceNode,
      data: data,
      target: edge.targetNode,
      type: "smoothstep",
      label: data.label,
      labelStyle: {
        fill: "black",
        fontFamily: "Roboto",
        width: "100%",
        fontWeight: "700",
      },
      labelBgStyle: { fill: data.bgFill },
      labelBgBorderRadius: 9,
      style: { strokeWidth: 2, zIndex: 1000 },
      markerEnd: { type: MarkerType.ArrowClosed, strokeWidth: 1 },
      labelBgPadding: [11, 2],
      className: "customEdge",
    };
  });
  return playbookEdges;
}

export default function PlaybookFlow({ playbook }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(undefined);

  const nodeTypes = useMemo(
    () => ({
      startEndNode: StartEndNode,
      actionNode: ActionNode,
      utilityNode: UtilityNode,
      decisionNode: DecisionNode,
      runPlaybookNode: RunPlaybookNode,
      formatNode: FormatNode,
      codeNode: CodeNode,
      promptNode: PromptNode,
      filterNode: FilterNode,
      missionControlNode: MissionControlNode
    }),
    []
  );

  const edgeTypes = useMemo(() => ({ playbook: PlaybookEdge }), []);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onNodeClick = (ev, node) => {
    setSelectedNode(node);
  };

  const onResetLayout = (ev) => {
    let playbookNodes = generateNodes(playbook);
    let playbookEdges = generateEdges(playbook);
    let layoutedElements = { nodes: playbookNodes, edges: playbookEdges };

    setNodes(layoutedElements.nodes);
    setEdges(layoutedElements.edges);
  };

  useEffect(() => {
    let playbookNodes = generateNodes(playbook);
    let playbookEdges = generateEdges(playbook);

    let layoutedElements = { nodes: playbookNodes, edges: playbookEdges };

    setNodes(layoutedElements.nodes);
    setEdges(layoutedElements.edges);
  }, [playbook, setEdges, setNodes]);

  return (
    <>
      <ReactFlow
        className="PlaybookViewer"
        nodes={nodes}
        nodeTypes={nodeTypes}
        edges={edges}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onConnect={onConnect}
        snapToGrid
        fitView
      >
        <PlaybookInfoCard playbook={playbook}></PlaybookInfoCard>
        {selectedNode ? <InfoCard playbook={playbook} node={selectedNode}></InfoCard> : <></>}
        <Controls>
          <LicenseControl></LicenseControl>
          <ResetLayoutControl
            onResetLayout={onResetLayout}
          ></ResetLayoutControl>
          <ImageDownloadControl onResetLayout={onResetLayout}></ImageDownloadControl>
          <VectorGraphicDownloadControl onResetLayout={onResetLayout}></VectorGraphicDownloadControl>
          <DocumentDownloadControl onResetLayout={onResetLayout}></DocumentDownloadControl>
        </Controls>
        <Background />
      </ReactFlow>
    </>
  );
}
